class ViewNode {
    constructor({
        uid = '',
        name = '',
        key = '',
        idKey= '',
        zoom = {min: 2, max: 18},
        region = {},
        organization = '',
        loaded=0} = {}) {

        this.uid = uid;
        this.name = name;
        this.key = key;
        this.idKey = idKey;
        this.zoom = zoom;
        this.region = { ...region };
        this.markerList = [];
        this.layer = null;
        this.selectedLayerType = null;
        this.organization = organization;
        this.loaded = loaded;
    }

    get selectedLayer() {
        if (this.layer && this.selectedLayerType) {
            return this.layer[this.selectedLayerType];
        }

        return null;
    }

    get selectedLayerData() {
        const l = this.selectedLayer;

        if (l && l.selectedLayerIndex >= 0) {
            return l.data[l.selectedLayerIndex];
        }

        return null;
    }

    get layerSelection() {
        if (this.layer && this.selectedLayerType) {
            const l = this.layer[this.selectedLayerType];

            if (l && l.selectedLayerIndex >= 0) {
                return [this.selectedLayerType, l.selectedLayerIndex];
            }
        }

        return [null, null];
    }

    updateKey = (prevKey) => {
        if (prevKey) {
            if (this.key) {
                this.key = prevKey + '/' + this.key;
            } else {
                this.key = prevKey;
            }
        }

        this.uid = this.organization + '/' + this.key;
    }

    validBounds = () => {
        return (this.region && this.region.bounds && this.region.bounds.isValid());
    }

    removeRegionLayer = () => {
        if (this.region) {
            if (this.region.boundsObj) {
                this.region.boundsObj.remove();
            }

            if (this.region.borderObj) {
                this.region.borderObj.remove();
            }
        }

        if (this.markerList) {
            for (const m of this.markerList) {
                if (m.obj){
                    m.obj.remove();
                }
            }

            this.markerList = [];
        }
    }

    removeOverlayLayer = (discardLayer=false) => {
        if (this.layer) {
            Object.entries(this.layer).forEach(([index, value]) => {
                for (const l of value.data) {
                    if (l.obj) {
                        l.obj.remove();

                        if (discardLayer) {
                            l.obj.off();
                            l.obj = null;
                        }
                    }
                }
            });
        }
    }
}

class ViewStack {
    constructor(val, updateViewStack) {
        this._default = new ViewNode(val);
        this._stack = [];
        this._updateViewStack = updateViewStack;
    }

    setMap = (map) => {
        this._map = map;
    }

    peek = (offset = 0) => {
        const len = this._stack.length;

        if (offset < 0) {
            offset = offset * -1;

            if (offset === 1) {
                return this._default;
            }

            offset = offset - 2;

            return (offset < len) ? this._stack[offset] : null;
        }

        const index = len - offset - 1;

        if (index === -1) {
            return this._default;
        } else if (index < -1) {
            return null;
        } else {
            return this._stack[index];
        }
    }

    push = (node) => {
        node = new ViewNode(node);

        const prev = this.peek();

        node.updateKey(prev.key);

        prev.removeRegionLayer();

        if (this._stack.length === 0) {
            this._default.region.bounds = this._map.getBounds();
            this._default.layer.base.data[0].obj.setOpacity(0.7);
        } else {
            prev.removeOverlayLayer(false);
        }

        this._stack.push(node);

        this._updateViewStack([]);

        this._requestZoomReset(false);
    }

    pushLayerObj = (l, bounds, latLngBounds) => {
        const zoomLevel = this._map.getBoundsZoom(bounds, false);

        this.push({
            name: l.feature.properties.name,
            key: l.feature.properties.key,
            idKey: l.feature.properties.key,
            zoom: {min: zoomLevel},
            region: {
                bounds: latLngBounds,
                maxBounds: latLngBounds.pad(0.2),
                borderData: l.toGeoJSON(),
            },
            organization: l.feature.properties.organization,
        });
    }

    popFrom = (index = -1) => {
        var spliceStart = 0;
        var spliceCount = 0;

        if (index === -1) {
            this.peek().removeRegionLayer();
            this.peek().removeOverlayLayer(true);
            spliceStart = this._stack.length - 1;
            spliceCount = 0;
            this._stack.pop();
        } else if (index === 0) {
            for (var v of this._stack) {
                v.removeRegionLayer();
                v.removeOverlayLayer(true);
            }

            spliceStart = 0;
            spliceCount = this._stack.length;

            this._default.removeRegionLayer();
            this._default.removeOverlayLayer(true);
        } else if (index > 0) {
            index = index - 1;

            for (var i = index; i < this._stack.length; ++i) {
                this._stack[i].removeRegionLayer();
                this._stack[i].removeOverlayLayer(true);
            }

            spliceStart = index;
            spliceCount = this._stack.length;
        }

        const spliceList = this._stack.splice(spliceStart, spliceCount);

        if (this._stack.length === 0 && this._default.layer.base.data[0].obj) {
            this._default.layer.base.data[0].obj.setOpacity(1.0);
        }

        this._updateViewStack(spliceList);

        this._requestZoomReset(true);
    }

    iterate = (fn) => {
        return this._stack.map(fn, this);
    }

    isDefaultBase = () => {
        return (this._stack.length === 0);
    }

    length = () => {
        return this._stack.length + 1;
    }

    _requestZoomReset = async (allowZoom) => {
        const view = this.peek();

        this._map.setMinZoom(view.zoom.min);

        if (this._default.region.bounds) {
            if (allowZoom) {
                if (view.validBounds()) {
                    this._map.fitBounds(view.region.bounds);
                }
            } else {
                if (this._map.getZoom() >= view.zoom.min) {
                    this._map.panTo(view.region.bounds.getCenter());
                } else if (view.validBounds()) {
                    this._map.fitBounds(view.region.bounds);
                }
            }
        }

        if (this._stack.length === 0) {
            this._default.region.bounds = null;
        }
    }
}

export default ViewStack;
